'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebounceValue, useEventListener } from 'usehooks-ts';
import type { TenorGif } from '../actions/gifs-action';
import { LoaderIcon } from '@/components/icons';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGifsInfinite } from '@/data/gifs/use-gifs';
import { cn } from '@/lib/utils';
type GifButtonProps = {
  gif: TenorGif;
  onSelect: () => void;
  isSelected?: boolean;
};

/**
 * Individual GIF button component that displays a GIF thumbnail
 * and handles click interactions.
 */
function GifButton(t0) {
  const $ = _c(10);
  const {
    gif,
    onSelect,
    isSelected
  } = t0;
  const t1 = isSelected && "ring-1 ring-primary ring-offset-1";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("overflow-hidden rounded-md bg-muted transition-all hover:opacity-80", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== gif.media_formats.tinygif.url || $[3] !== gif.title) {
    t3 = <img src={gif.media_formats.tinygif.url} alt={gif.title} className="aspect-square size-full object-cover" loading="lazy" />;
    $[2] = gif.media_formats.tinygif.url;
    $[3] = gif.title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isSelected || $[6] !== onSelect || $[7] !== t2 || $[8] !== t3) {
    t4 = <button type="button" onClick={onSelect} className={t2} role="gridcell" aria-selected={isSelected}>{t3}</button>;
    $[5] = isSelected;
    $[6] = onSelect;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
type GifPickerProps = {
  handleGifSelectAction: (gif: {
    url: string;
    title: string;
  }) => void;
  className?: string;
};

/**
 * GIF picker component with search and infinite scroll functionality.
 * Features:
 * - Search input for finding GIFs
 * - Grid layout of GIF thumbnails
 * - Infinite scroll with 50px pre-loading margin
 * - Loading states for initial load and pagination
 * - Keyboard navigation with arrow keys and Enter
 */
export function GifPicker(t0) {
  const $ = _c(32);
  const {
    handleGifSelectAction,
    className
  } = t0;
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const inputRef = useRef(null);
  const [debouncedSearch] = useDebounceValue(search, 200);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "200px 0px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    ref: loadMoreRef,
    inView
  } = useInView(t1);
  const {
    gifs,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useGifsInfinite(debouncedSearch);
  let t2;
  let t3;
  if ($[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== inView) {
    t2 = () => {
      if (inView && hasNextPage) {
        fetchNextPage();
      }
    };
    t3 = [inView, hasNextPage, fetchNextPage];
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = inView;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== gifs.length) {
    t4 = e => {
      if (e.key === "ArrowDown" && gifs.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        setIsNavigating(true);
        setSelectedIndex(0);
        inputRef.current?.blur();
      }
    };
    $[6] = gifs.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleInputKeyDown = t4;
  let t5;
  if ($[8] !== gifs || $[9] !== handleGifSelectAction || $[10] !== isLoading || $[11] !== isNavigating || $[12] !== selectedIndex) {
    t5 = e_0 => {
      if (gifs.length === 0 || isLoading || !isNavigating) {
        return;
      }
      if (document.activeElement === inputRef.current) {
        return;
      }
      bb37: switch (e_0.key) {
        case "ArrowRight":
          {
            e_0.preventDefault();
            setSelectedIndex(prev_2 => (prev_2 + 1) % gifs.length);
            break bb37;
          }
        case "ArrowLeft":
          {
            e_0.preventDefault();
            setSelectedIndex(prev_1 => (prev_1 - 1 + gifs.length) % gifs.length);
            break bb37;
          }
        case "ArrowDown":
          {
            e_0.preventDefault();
            setSelectedIndex(prev_0 => {
              const newIndex_0 = prev_0 + 2;
              return newIndex_0 < gifs.length ? newIndex_0 : prev_0;
            });
            break bb37;
          }
        case "ArrowUp":
          {
            e_0.preventDefault();
            setSelectedIndex(prev => {
              const newIndex = prev - 2;
              if (newIndex < 0) {
                setIsNavigating(false);
                inputRef.current?.focus();
                return 0;
              }
              return newIndex;
            });
            break bb37;
          }
        case "Enter":
          {
            e_0.preventDefault();
            const selectedGif = gifs[selectedIndex];
            if (selectedGif) {
              handleGifSelectAction({
                url: selectedGif.media_formats.tinygif.url,
                title: selectedGif.title
              });
            }
            break bb37;
          }
        case "Escape":
          {
            e_0.preventDefault();
            setIsNavigating(false);
            inputRef.current?.focus();
          }
      }
    };
    $[8] = gifs;
    $[9] = handleGifSelectAction;
    $[10] = isLoading;
    $[11] = isNavigating;
    $[12] = selectedIndex;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEventListener("keydown", t5);
  let t6;
  if ($[14] !== className) {
    t6 = cn("flex flex-col gap-4 p-2 h-full", className);
    $[14] = className;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_1 => {
      setSearch(e_1.target.value);
      setSelectedIndex(0);
      setIsNavigating(false);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== handleInputKeyDown || $[18] !== search) {
    t8 = <div className="space-y-6"><Input ref={inputRef} placeholder="Search GIFs..." value={search} onChange={t7} onKeyDown={handleInputKeyDown} className="h-9" /></div>;
    $[17] = handleInputKeyDown;
    $[18] = search;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== gifs || $[21] !== handleGifSelectAction || $[22] !== isFetchingNextPage || $[23] !== isLoading || $[24] !== isNavigating || $[25] !== loadMoreRef || $[26] !== selectedIndex) {
    t9 = <ScrollArea><div className="pr-4">{isLoading ? <div className="flex h-full items-center justify-center"><LoaderIcon /></div> : <><div className="grid grid-cols-2 gap-2 p-1" role="grid">{gifs.map((gif, index) => <GifButton key={gif.id} gif={gif} isSelected={isNavigating && index === selectedIndex} onSelect={() => handleGifSelectAction({
              url: gif.media_formats.tinygif.url,
              title: gif.title
            })} />)}</div><div ref={loadMoreRef} className="mt-4 flex justify-center">{isFetchingNextPage ? <LoaderIcon /> : null}</div></>}</div></ScrollArea>;
    $[20] = gifs;
    $[21] = handleGifSelectAction;
    $[22] = isFetchingNextPage;
    $[23] = isLoading;
    $[24] = isNavigating;
    $[25] = loadMoreRef;
    $[26] = selectedIndex;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t6 || $[29] !== t8 || $[30] !== t9) {
    t10 = <div className={t6}>{t8}{t9}</div>;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}